import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Heading,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { resolvePublicProposal } from '../../utils/resolvePublicProposal';
import { categoriaLabels } from '../proposals-school/proposalsSchoolUi';
import {
  resolveProposalTheme,
  resolveProposalThemeKeyFromPath,
} from '../proposals-standalone/proposalThemes';

/**
 * Testo usato dai filtri chip (match case-insensitive su sotto-stringhe).
 * Include titolo, descrizioni, keyword, etichetta categoria, slug categoria e
 * enum tipologia (experienceType / activityType / format) così le keyword restano
 * utili ma non sono l’unico legame con «Categoria attività» in admin.
 */
const buildChipFilterHaystack = (resolved) => {
  const kw = Array.isArray(resolved.keywords)
    ? resolved.keywords.join(' ')
    : '';
  const cat =
    resolved.category && categoriaLabels[resolved.category]
      ? categoriaLabels[resolved.category]
      : resolved.category || '';
  const catSlug = resolved.category ? String(resolved.category) : '';
  const exp = resolved.experienceType ? String(resolved.experienceType) : '';
  const act = resolved.activityType ? String(resolved.activityType) : '';
  const fmt = resolved.format ? String(resolved.format) : '';
  const loc = resolved.location ? String(resolved.location) : '';
  return `${resolved.title} ${resolved.shortDescription || ''} ${resolved.fullDescription || ''} ${kw} ${cat} ${catSlug} ${exp} ${act} ${fmt} ${loc}`
    .trim()
    .toLowerCase();
};

/**
 * @param {{ key?: string, keywords?: string[], matchCategories?: string[], matchExperienceTypes?: string[], matchActivityTypes?: string[] }} chip
 * @param {ReturnType<typeof resolvePublicProposal>} resolved
 */
const chipMatchesResolved = (chip, resolved) => {
  if (!chip || chip.key === 'all') return true;
  const cat = resolved.category;
  if (
    Array.isArray(chip.matchCategories) &&
    chip.matchCategories.length &&
    cat &&
    chip.matchCategories.includes(cat)
  ) {
    return true;
  }
  const exp = resolved.experienceType;
  if (
    Array.isArray(chip.matchExperienceTypes) &&
    chip.matchExperienceTypes.length &&
    exp &&
    chip.matchExperienceTypes.includes(exp)
  ) {
    return true;
  }
  const act = resolved.activityType;
  if (
    Array.isArray(chip.matchActivityTypes) &&
    chip.matchActivityTypes.length &&
    act &&
    chip.matchActivityTypes.includes(act)
  ) {
    return true;
  }
  const keywords = (chip.keywords || []).map((k) => String(k).toLowerCase());
  if (!keywords.length) return false;
  const hay = buildChipFilterHaystack(resolved);
  return keywords.some((k) => hay.includes(k));
};

const getMediaUrl = (media) => {
  const mediaUrl = media?.url;
  if (!mediaUrl) return null;
  if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
    return mediaUrl;
  }
  const base = process.env.REACT_APP_STRAPI_API_URL || '';
  return `${base}${mediaUrl}`;
};

export default function DynamicPropostePubblicheSection({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [activeChip, setActiveChip] = useState('all');
  const proposteConfig = config?.propostePubbliche || {};
  const isEnabled = Boolean(proposteConfig.enabled);
  const filterChips = Array.isArray(proposteConfig.filterChips)
    ? proposteConfig.filterChips
    : [];
  const filtersEnabled = Boolean(proposteConfig.filtersEnabled);

  useEffect(() => {
    setActiveChip('all');
  }, [proposteConfig.landing, proposteConfig.limit]);

  useEffect(() => {
    if (!isEnabled || !proposteConfig.landing) return undefined;

    const controller = new AbortController();
    const params = new URLSearchParams({
      landing: proposteConfig.landing,
      propostaAttiva: 'true',
      page: '1',
      pageSize: String(proposteConfig.limit || 6),
    });

    const loadProposals = async () => {
      setLoading(true);
      setHasError(false);
      try {
        const response = await fetch(
          `/api/proposte-pubbliche?${params.toString()}`,
          {
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        setItems(Array.isArray(payload?.data) ? payload.data : []);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          setHasError(true);
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProposals();
    return () => controller.abort();
  }, [isEnabled, proposteConfig.landing, proposteConfig.limit]);

  if (!isEnabled) return null;

  const fallbackText =
    proposteConfig.fallbackText || 'Le proposte saranno disponibili a breve.';
  const themePath = `/${proposteConfig.landing || 'proposte-scuole'}`;
  const theme = resolveProposalTheme(
    resolveProposalThemeKeyFromPath(themePath)
  );

  const filteredItems = useMemo(() => {
    if (!filtersEnabled || !filterChips.length) return items;
    const chip = filterChips.find((c) => c.key === activeChip);
    if (!chip || chip.key === 'all') return items;
    const hasStructured =
      (Array.isArray(chip.matchCategories) &&
        chip.matchCategories.length > 0) ||
      (Array.isArray(chip.matchExperienceTypes) &&
        chip.matchExperienceTypes.length > 0) ||
      (Array.isArray(chip.matchActivityTypes) &&
        chip.matchActivityTypes.length > 0);
    const hasKeywords = (chip.keywords || []).length > 0;
    if (!hasStructured && !hasKeywords) return items;
    return items.filter((item) => {
      const resolved = resolvePublicProposal(item);
      return chipMatchesResolved(chip, resolved);
    });
  }, [items, activeChip, filtersEnabled, filterChips]);

  return (
    <Box
      id={proposteConfig.landing}
      scrollMarginTop="200px"
      bg={theme.colors.bg}
      py={{ base: 10, md: 14 }}
    >
      <Container maxW="container.xl">
        <Stack spacing={6}>
          <Box>
            <Heading
              as="h2"
              size="lg"
              color={theme.colors.text}
              fontFamily={theme.fontHeading}
            >
              {proposteConfig.title || 'Proposte pubbliche'}
            </Heading>
            {proposteConfig.subtitle ? (
              <Text
                mt={2}
                color={theme.colors.muted}
                fontFamily={theme.fontBody}
              >
                {proposteConfig.subtitle}
              </Text>
            ) : null}
          </Box>

          {filtersEnabled && filterChips.length > 0 && !loading ? (
            <Wrap spacing={2}>
              {filterChips.map((chip) => {
                const selected = chip.key === activeChip;
                return (
                  <WrapItem key={chip.key}>
                    <Button
                      size="sm"
                      borderRadius="full"
                      variant="outline"
                      borderColor={theme.colors.border}
                      bg={selected ? theme.colors.accent : 'transparent'}
                      color={theme.colors.text}
                      fontWeight={selected ? '800' : '600'}
                      fontFamily={theme.fontBody}
                      _hover={{
                        bg: selected
                          ? theme.colors.accent
                          : theme.colors.surface,
                      }}
                      onClick={() => setActiveChip(chip.key)}
                    >
                      {chip.label}
                    </Button>
                  </WrapItem>
                );
              })}
            </Wrap>
          ) : null}

          {loading ? (
            <Stack direction="row" align="center">
              <Spinner color="#36a9e1" />
              <Text>Caricamento proposte...</Text>
            </Stack>
          ) : null}

          {!loading && items.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {filteredItems.map((item) => {
                const resolved = resolvePublicProposal(item);
                const detailPath = `/${proposteConfig.landing}/proposte/${item.slug}`;
                return (
                  <Box
                    as={RouterLink}
                    to={detailPath}
                    key={item.slug || item.documentId}
                    role="group"
                    bg={theme.colors.surface}
                    borderWidth="2px"
                    borderColor={theme.colors.border}
                    borderRadius="24px"
                    overflow="hidden"
                    boxShadow="0 18px 40px rgba(0,0,0,0.2)"
                    textDecoration="none"
                    transition="transform 180ms ease, box-shadow 180ms ease"
                    _hover={{
                      transform: 'translateY(-8px)',
                      boxShadow: '0 26px 56px rgba(0,0,0,0.32)',
                    }}
                  >
                    {getMediaUrl(item.thumbnail) ? (
                      <Box
                        bg="blackAlpha.50"
                        borderBottom="10px solid"
                        borderColor={theme.colors.surface}
                      >
                        <Image
                          src={getMediaUrl(item.thumbnail)}
                          alt={resolved.title}
                          w="100%"
                          h="210px"
                          objectFit="cover"
                          transform="scale(1.02)"
                          transition="transform 220ms ease"
                          _groupHover={{ transform: 'scale(1.06)' }}
                        />
                      </Box>
                    ) : null}
                    <Box
                      bg="white"
                      p={5}
                      pt={getMediaUrl(item.thumbnail) ? 4 : 6}
                    >
                      <Heading
                        as="h3"
                        fontSize="lg"
                        textAlign="center"
                        color={theme.colors.onLightTitle ?? theme.colors.text}
                        fontFamily={theme.fontHeading}
                        mb={2}
                        noOfLines={3}
                      >
                        {resolved.title}
                      </Heading>
                      {resolved.shortDescription ? (
                        <Text
                          fontSize="sm"
                          textAlign="center"
                          color={
                            theme.colors.onLightMuted ?? theme.colors.muted
                          }
                          noOfLines={3}
                        >
                          {resolved.shortDescription}
                        </Text>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>
          ) : null}

          {!loading &&
          items.length > 0 &&
          filtersEnabled &&
          filteredItems.length === 0 ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text>
                Nessuna proposta corrisponde al filtro selezionato. Prova
                un&apos;altra categoria o «Tutte».
              </Text>
            </Alert>
          ) : null}

          {!loading && (hasError || items.length === 0) ? (
            <Alert status={hasError ? 'warning' : 'info'} borderRadius="md">
              <AlertIcon />
              <Text>{fallbackText}</Text>
            </Alert>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}
